import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Check, X, Eye, EyeOff, Inbox, Filter, RefreshCw, ExternalLink, Clock, AlertCircle, ListChecks } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const STATUS_TONES = {
  draft: "cyan",
  needs_review: "amber",
  approved: "emerald",
  rejected: "rose",
  published: "violet",
};
const STATUS_LABELS = {
  draft: "Draft",
  needs_review: "Needs Review",
  approved: "Approved",
  rejected: "Rejected",
  published: "Published",
};

const AGENT_LABELS = {
  scouting_report: "Scouting Report",
  draft_strategy: "Draft Strategy",
  team_fit_explanation: "Team Fit Explanation",
  interview_evaluator: "Interview Evaluator",
  interview_summary: "Interview Summary",
  player_360_synthesis: "Player 360°",
  report_draft: "Report Draft",
  player_narrative: "Player Narrative",
  weekly_progress: "Weekly Progress",
};

const REF_ROUTES = {
  evaluation: (pid) => `/app/players/${pid}/evaluation`,
  team_fit: (pid) => `/app/players/${pid}/team-fits`,
  draft_plan: (pid) => `/app/players/${pid}/draft-plan`,
  report: (pid) => `/app/players/${pid}/reports`,
  interview_session: (pid) => `/app/players/${pid}/interview-prep`,
  player: (pid) => `/app/players/${pid}/profile`,
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AICockpitPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ by_status: {}, by_agent: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [players, setPlayers] = useState({});
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, countsRes, playersRes] = await Promise.all([
        api.get("/agent-outputs", { params: { limit: 200 } }),
        api.get("/agent-outputs/counts"),
        api.get("/players"),
      ]);
      setItems(listRes.data.items);
      setCounts(countsRes.data);
      const map = {};
      (playersRes.data || []).forEach((p) => { map[p.id] = p; });
      setPlayers(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cockpit");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((x) => {
      if (statusFilter !== "all" && x.status !== statusFilter) return false;
      if (agentFilter !== "all" && x.agent_key !== agentFilter) return false;
      return true;
    });
  }, [items, statusFilter, agentFilter]);

  const pendingCount = (counts.by_status?.draft || 0) + (counts.by_status?.needs_review || 0);

  const patchStatus = async (id, status) => {
    try {
      const r = await api.patch(`/agent-outputs/${id}`, { status });
      setItems((items) => items.map((x) => x.id === id ? r.data : x));
      if (selected?.id === id) setSelected(r.data);
      toast.success(`Marked ${STATUS_LABELS[status] || status}`);
      await loadCountsOnly();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const loadCountsOnly = async () => {
    try {
      const c = await api.get("/agent-outputs/counts");
      setCounts(c.data);
    } catch (_e) { /* noop */ }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-[1600px] mx-auto" data-testid="ai-cockpit-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2.5">
            <Sparkles className="h-5 w-5 text-cyan-300" />
            <h1 className="text-2xl sm:text-3xl font-semibold text-1 tracking-tight">AI Activity Cockpit</h1>
            <StatusPill tone="neutral">Admin</StatusPill>
          </div>
          <p className="text-2 text-sm mt-1">Every AI production across the 9 agents — routed to one inbox.</p>
        </div>
        <Button data-testid="cockpit-refresh" onClick={load} variant="ghost" className="text-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatTile icon={Inbox} label="Total events" value={items.length} tone="cyan" testId="cockpit-stat-total" />
        <StatTile icon={Clock} label="Pending review" value={pendingCount} tone="amber" testId="cockpit-stat-pending" />
        <StatTile icon={Check} label="Approved" value={counts.by_status?.approved || 0} tone="emerald" testId="cockpit-stat-approved" />
        <StatTile icon={Eye} label="Published" value={counts.by_status?.published || 0} tone="violet" testId="cockpit-stat-published" />
      </div>

      {/* Filters */}
      <GlassCard className="p-4 mb-4" testId="cockpit-filters">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-3 text-xs"><Filter className="h-3.5 w-3.5" /> STATUS</div>
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="bg-white/5 border border-white/10">
              <TabsTrigger value="all" data-testid="filter-status-all">All</TabsTrigger>
              {Object.keys(STATUS_LABELS).map((s) => (
                <TabsTrigger key={s} value={s} data-testid={`filter-status-${s}`}>
                  {STATUS_LABELS[s]} {counts.by_status?.[s] ? `(${counts.by_status[s]})` : ""}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        <div className="flex items-start gap-3 flex-wrap mt-3">
          <div className="flex items-center gap-1.5 text-3 text-xs"><ListChecks className="h-3.5 w-3.5" /> AGENT</div>
          <Tabs value={agentFilter} onValueChange={setAgentFilter}>
            <TabsList className="bg-white/5 border border-white/10 flex-wrap h-auto">
              <TabsTrigger value="all" data-testid="filter-agent-all">All</TabsTrigger>
              {Object.keys(AGENT_LABELS).filter((k) => counts.by_agent?.[k]).map((k) => (
                <TabsTrigger key={k} value={k} data-testid={`filter-agent-${k}`}>
                  {AGENT_LABELS[k]} ({counts.by_agent[k]})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </GlassCard>

      {/* Feed */}
      <GlassCard className="p-0 overflow-hidden" testId="cockpit-feed">
        {filtered.length === 0 ? (
          <div className="p-10">
            <EmptyState icon={Inbox} title="Nothing to review" description="No AI activity matches your filters." />
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((x) => {
              const player = players[x.player_id];
              const route = REF_ROUTES[x.ref_type]?.(x.player_id);
              return (
                <button
                  key={x.id}
                  onClick={() => setSelected(x)}
                  data-testid={`cockpit-row-${x.id}`}
                  className="w-full text-left px-5 py-3.5 hover:bg-white/[0.03] transition flex items-start gap-4 group"
                >
                  <div className="shrink-0 mt-0.5">
                    <StatusPill tone={STATUS_TONES[x.status] || "neutral"}>{STATUS_LABELS[x.status] || x.status}</StatusPill>
                  </div>
                  <div className="shrink-0 w-40 min-w-0">
                    <div className="text-1 text-sm font-medium truncate">{AGENT_LABELS[x.agent_key] || x.agent_key}</div>
                    <div className="text-3 text-[11px]">{timeAgo(x.updated_at || x.created_at)}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-1 text-sm truncate">{x.summary || "—"}</div>
                    <div className="text-3 text-[11px] mt-0.5 flex items-center gap-1.5">
                      {player && <span className="truncate">{player.full_name} · {player.position}</span>}
                      {x.visible_to_family && <span className="text-emerald-300 inline-flex items-center gap-1"><Eye className="h-3 w-3" />visible</span>}
                      {x.error_md && <span className="text-rose-300 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" />error</span>}
                    </div>
                  </div>
                  {route && (
                    <Link
                      to={route}
                      onClick={(e) => e.stopPropagation()}
                      className="text-2 hover:text-cyan-300 text-xs opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1 shrink-0"
                      data-testid={`cockpit-goto-${x.id}`}
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </GlassCard>

      {selected && (
        <DetailDialog
          open={!!selected}
          onOpenChange={(v) => !v && setSelected(null)}
          item={selected}
          player={players[selected.player_id]}
          onPatch={(status) => patchStatus(selected.id, status)}
        />
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone, testId }) {
  const toneClass = {
    cyan: "from-cyan-400/15 to-cyan-400/[0.04] text-cyan-200",
    amber: "from-amber-400/15 to-amber-400/[0.04] text-amber-200",
    emerald: "from-emerald-400/15 to-emerald-400/[0.04] text-emerald-200",
    violet: "from-violet-400/15 to-violet-400/[0.04] text-violet-200",
  }[tone] || "from-white/10 to-white/[0.03] text-white/80";
  return (
    <div data-testid={testId} className={`rounded-xl border border-white/10 bg-gradient-to-br ${toneClass} p-4`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-1 text-3xl font-semibold mt-1.5">{value}</div>
    </div>
  );
}

function DetailDialog({ open, onOpenChange, item, player, onPatch }) {
  const NEXT_ACTIONS = {
    draft: [
      { label: "Approve", status: "approved", tone: "emerald", icon: Check },
      { label: "Reject", status: "rejected", tone: "rose", icon: X },
    ],
    needs_review: [
      { label: "Approve", status: "approved", tone: "emerald", icon: Check },
      { label: "Reject", status: "rejected", tone: "rose", icon: X },
    ],
    approved: [
      { label: "Publish", status: "published", tone: "violet", icon: Eye },
      { label: "Reject", status: "rejected", tone: "rose", icon: X },
    ],
    rejected: [
      { label: "Move back to draft", status: "draft", tone: "cyan", icon: RefreshCw },
    ],
    published: [
      { label: "Unpublish", status: "approved", tone: "amber", icon: EyeOff },
    ],
  };
  const actions = NEXT_ACTIONS[item.status] || [];
  const route = REF_ROUTES[item.ref_type]?.(item.player_id);
  const payload = item.payload || {};
  const contentMd = payload.content_md || payload.explanation_md || payload.positioning_md || payload.report_md || "";
  const toneClasses = {
    emerald: "cta-primary bg-emerald-400 text-[#04111A] hover:bg-emerald-300",
    rose: "bg-rose-500/20 text-rose-200 hover:bg-rose-500/30",
    violet: "bg-violet-500/25 text-violet-100 hover:bg-violet-500/40",
    cyan: "cta-primary",
    amber: "bg-amber-500/20 text-amber-100 hover:bg-amber-500/30",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[color:var(--canvas-2)]/95 backdrop-blur-2xl border-white/10" data-testid="cockpit-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-1 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-300" />
            {AGENT_LABELS[item.agent_key] || item.agent_key}
            <StatusPill tone={STATUS_TONES[item.status] || "neutral"}>{STATUS_LABELS[item.status] || item.status}</StatusPill>
          </DialogTitle>
          <DialogDescription className="text-3 text-xs">
            {player && <>For <span className="text-1 font-medium">{player.full_name}</span> · {player.position} · </>}
            Created {timeAgo(item.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <Field label="Ref" value={`${item.ref_type || "—"} / ${item.ref_id?.slice(0, 8) || "—"}`} />
            <Field label="Visible to family" value={item.visible_to_family ? "Yes" : "No"} />
            {item.approved_at && <Field label="Approved" value={new Date(item.approved_at).toLocaleString()} />}
            {item.rejected_at && <Field label="Rejected" value={new Date(item.rejected_at).toLocaleString()} />}
            {item.published_at && <Field label="Published" value={new Date(item.published_at).toLocaleString()} />}
          </div>

          {item.summary && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="text-3 text-[10px] uppercase tracking-wider mb-1">Summary</div>
              <div className="text-1 text-sm">{item.summary}</div>
            </div>
          )}

          {contentMd && (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 markdown-body" data-testid="cockpit-detail-content">
              <div className="text-3 text-[10px] uppercase tracking-wider mb-2">AI Output</div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentMd.slice(0, 8000)}</ReactMarkdown>
            </div>
          )}

          {item.error_md && (
            <div className="rounded-xl border border-rose-400/30 bg-rose-500/5 p-4">
              <div className="text-rose-200 text-[11px] uppercase tracking-wider mb-1 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Error</div>
              <div className="text-2 text-sm whitespace-pre-wrap">{item.error_md}</div>
            </div>
          )}

          {route && (
            <Link to={route} className="inline-flex items-center gap-1.5 text-cyan-300 text-sm hover:underline">
              Open the source resource <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {actions.map((a) => {
            const Icon = a.icon;
            return (
              <Button
                key={a.status}
                data-testid={`cockpit-detail-action-${a.status}`}
                onClick={() => onPatch(a.status)}
                className={toneClasses[a.tone] || "cta-primary"}
              >
                <Icon className="h-4 w-4" /> {a.label}
              </Button>
            );
          })}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-3 text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-1 text-sm mt-0.5 break-all">{value}</div>
    </div>
  );
}
